/**
 * ✅ Migrated to authStore_v1 - 2026-01-07
 * 
 * Authentication Hook - Simplified Wrapper
 * 
 * Single hook that provides raw data from authStore_v1.
 * For new code, use authStore_v1 directly with optimized selectors.
 * 
 * @example
 * const auth = useAuth();
 * // Access: auth.user, auth.selectedOrg, auth.token, auth.isAuthenticated
 * // Actions: auth.logout()
 */
import { useCookies } from 'react-cookie';
import {
  useAuthStoreV1,
  useIsAuthenticated as useIsAuthenticatedV1,
  type User,
  type Organization
} from '@/stores/authStore_v1';
import { useShallow } from 'zustand/react/shallow';
import { clearApiAuthToken } from '@/dataProvider/axios';
import { resetMultiAgentForTenantChange } from '@/stores/multiAgentStore';

// Export types for compatibility
export type AuthUser = User;
export type AuthOrganization = Organization;

/**
 * Main auth hook - Returns raw data from authStore_v1
 * 
 * @example
 * const auth = useAuth();
 * console.log(auth.user?.email);
 * console.log(auth.selectedOrg?.orgName);
 * auth.logout();
 */
export function useAuth() {
  const [cookies, , removeCookie] = useCookies(['auth_token']);
  const isAuthenticated = useIsAuthenticatedV1();
  
  // Get raw data from store (no mapping, no transformation)
  const { user, organizations, selectedOrg, clearAll } = useAuthStoreV1(
    useShallow((s) => ({
      user: s.user,
      organizations: s.organizations,
      selectedOrg: s.selectedOrg,
      clearAll: s.clearAll,
    }))
  );
  const token = cookies.auth_token || null;
  
  /**
   * Handle logout with cleanup
   */
  const logout = () => {
    resetMultiAgentForTenantChange();

    // Clear cookie
    removeCookie('auth_token', { path: '/' });
    
    // Clear axios token
    clearApiAuthToken();
    
    // Clear store
    clearAll();
  };
  
  return {
    // Raw data from store (no mapping)
    token,
    user,
    organizations,
    selectedOrg,
    isAuthenticated,
    
    // Computed values
    isSalesforceConnected: !!selectedOrg,
    
    // Actions
    logout,
  };
}

